import { useCallback } from 'react';
import { useEntitlements } from './useEntitlements';
import { useAuthStore } from '../stores/authStore';
import { ExportFormat } from '../domains/access/models/Entitlements';
import { getGravityUserStatus } from './useAuth';

export interface ValidationResult {
  allowed: boolean;
  reason?: string;
  upgradeRequired?: boolean;
  requireSignIn?: boolean;
}

export function usePlanValidation() {
  const { entitlements, canExportFormat, canShareWithGuests, canInviteByEmail, getShapeLimit } = useEntitlements();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const validateExportFormat = useCallback((format: string): ValidationResult => {
    if (!entitlements) {
      if (!isAuthenticated) {
        return { allowed: false, requireSignIn: true };
      }
      return { allowed: false, reason: 'Entitlements not loaded' };
    }

    const status = typeof getGravityUserStatus === 'function' ? getGravityUserStatus() : null;
    const isDemoUser = (status || '').toUpperCase() === 'DEMO';
    const isFreePlan = entitlements.planType === 'free';
    if (isFreePlan || isDemoUser) {
      return {
        allowed: false,
        reason: `Export format '${format}' requires an upgrade`,
        upgradeRequired: true
      };
    }

    const allowed = canExportFormat(format as ExportFormat);
    if (!allowed) {
      return {
        allowed: false,
        reason: `Export format '${format}' is not allowed for your ${entitlements.planType} plan`,
        upgradeRequired: entitlements.planType === 'free'
      };
    }

    return { allowed: true };
  }, [entitlements, canExportFormat, isAuthenticated]);

  const validateSharingMode = useCallback((mode: 'team_only' | 'fine_grained'): ValidationResult => {
    if (!entitlements) {
      return { allowed: false, reason: 'Entitlements not loaded' };
    }

    const allowed = mode === 'team_only' || canShareWithGuests();
    if (!allowed) {
      return {
        allowed: false,
        reason: `Fine-grained sharing is not allowed for your ${entitlements.planType} plan`,
        upgradeRequired: entitlements.planType === 'free'
      };
    }

    return { allowed: true };
  }, [entitlements, canShareWithGuests]);

  const validateEmailInvite = useCallback((): ValidationResult => {
    if (!entitlements) {
      return { allowed: false, reason: 'Entitlements not loaded' };
    }

    const allowed = canInviteByEmail();
    if (!allowed) {
      return {
        allowed: false,
        reason: `Email invites are not allowed for your ${entitlements.planType} plan`,
        upgradeRequired: entitlements.planType === 'free'
      };
    }

    return { allowed: true };
  }, [entitlements, canInviteByEmail]);

  const validateShapeLimit = useCallback((currentCount: number, additionalShapes: number = 1): ValidationResult => {
    if (!entitlements) {
      return { allowed: false, reason: 'Entitlements not loaded' };
    }

    const limit = getShapeLimit();
    if (limit === null) {
      return { allowed: true }; // No limit
    }

    const newCount = currentCount + additionalShapes;
    if (newCount > limit) {
      return {
        allowed: false,
        reason: `Adding ${additionalShapes} shape(s) would exceed your limit of ${limit} shapes`,
        upgradeRequired: entitlements.planType === 'free'
      };
    }

    return { allowed: true };
  }, [entitlements, getShapeLimit]);

  const validateAction = useCallback((action: string, params: any = {}): ValidationResult => {
    switch (action) {
      case 'export':
        return validateExportFormat(params.format);
      case 'share':
        return validateSharingMode(params.mode);
      case 'invite_email':
        return validateEmailInvite();
      case 'add_shape':
        return validateShapeLimit(params.currentCount, params.additionalShapes);
      default:
        return { allowed: true };
    }
  }, [validateExportFormat, validateSharingMode, validateEmailInvite, validateShapeLimit]);

  return {
    validateExportFormat,
    validateSharingMode,
    validateEmailInvite,
    validateShapeLimit,
    validateAction,
  };
}
